import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import FPlanningJourney from '../core/FPlanningJourney';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import ForecastBaselineWizard from './ForecastBaselineWizard';
import {
  completeForecasting,
  createForecastBatch,
  deleteForecastBatch,
  fetchForecastingManager,
} from './bieforecasting.apis';
import { IForecastingBatchRow, IForecastingManager } from './bieforecasting.models';
import ForecastingBatchGrid from './ForecastingBatchGrid';

type State = {
  loading: boolean;
  error: string;
  success: string;
  warning: string;
  phases: any[];
  manager: IForecastingManager | null;
  selected: IForecastingBatchRow | null;
  selectedBatchId: number | null;
  createOpen: boolean;
  batchname: string;
  historymonths: number;
  completeOpen: boolean;
  completionNotes: string;
};

export default class BieForecastingWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    loading: false,
    error: '',
    success: '',
    warning: '',
    phases: [],
    manager: null,
    selected: null,
    selectedBatchId: null,
    createOpen: false,
    batchname: '',
    historymonths: 60,
    completeOpen: false,
    completionNotes: '',
  };

  componentDidMount(): void {
    this.initialize();
  }

  private getPlanningCycleId = (): number => Number(this.props.plancontext?.pci || 0);

  private initialize = async (): Promise<void> => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) {
      this.setState({ error: 'Planning Cycle context is required.' });
      return;
    }

    this.setState({ loading: true, error: '', success: '', warning: '' });
    try {
      const [phases, manager] = await Promise.all([
        fetchPlanningJourneyPhases(),
        fetchForecastingManager(planningcycleid),
      ]);
      this.setState({ phases, manager });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  public refresh = async (): Promise<void> => this.initialize();

  private openCreate = (): void => {
    const context = this.state.manager?.context;
    this.setState({
      createOpen: true,
      batchname: `${context?.planningcyclename || context?.fiscalyear || ''} Forecast`,
      historymonths: 60,
    });
  };

  private createBatch = async (): Promise<void> => {
    const planningcycleid = this.getPlanningCycleId();
    const batchname = this.state.batchname.trim();
    if (!batchname) return;

    this.setState({ loading: true, error: '', success: '' });
    try {
      const result: any = await createForecastBatch(
        planningcycleid,
        batchname,
        this.state.historymonths,
      );
      const batchid = Number(result?.rid ?? result?.id ?? result?.batchid ?? 0);
      this.setState({
        createOpen: false,
        selectedBatchId: batchid || null,
        success: 'Forecast Batch created.',
      });
      if (!batchid) await this.initialize();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private deleteBatch = async (row: IForecastingBatchRow): Promise<void> => {
    if (!row.deletable) return;
    if (!window.confirm(`Delete Draft Forecast Batch “${row.nam}”?`)) return;

    this.setState({ loading: true, error: '', success: '' });
    try {
      await deleteForecastBatch(row.rid);
      this.setState({ success: 'Forecast Batch deleted.' });
      await this.initialize();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private complete = async (): Promise<void> => {
    const planningcycleid = this.getPlanningCycleId();
    this.setState({ loading: true, error: '', success: '' });
    try {
      const result: any = await completeForecasting(planningcycleid, this.state.completionNotes);
      const nextpage = result?.actions?.nextpage ?? result?.transition?.nextpage ?? this.state.manager?.actions.nextpage;
      this.setState({ completeOpen: false, success: 'Forecasting completed. Opening BIE Simulation.' });
      if (nextpage) this.props.setPage(nextpage);
      else await this.initialize();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private renderSummary = (): React.ReactNode => {
    const summary = this.state.manager?.summary;
    if (!summary) return null;
    const cards = [
      ['All Batches', summary.batchcount],
      ['Draft', summary.draftcount],
      ['Generated', summary.generatedcount],
      ['Quality Validated', summary.qualityvalidatedcount],
      ['Published', summary.publishedcount],
      ['Usable', summary.usablecount],
    ];
    return (
      <Grid container spacing={2}>
        {cards.map(([label, value]) => (
          <Grid key={String(label)} size={{ xs: 6, md: 2 }}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  private renderManager = (): React.ReactNode => {
    const manager = this.state.manager;
    if (!manager) {
      return !this.state.loading
        ? <Alert severity="info">Forecasting manager is not available.</Alert>
        : null;
    }

    return (
      <Stack spacing={2}>
        {this.renderSummary()}
        <ForecastingBatchGrid
          rows={manager.batches}
          loading={this.state.loading}
          selectedRowId={this.state.selected?.rid ?? null}
          onSelect={(selected) => this.setState({ selected })}
          canCreate={manager.actions.canCreateBatch}
          onCreate={this.openCreate}
          onOpen={(row) => this.setState({ selectedBatchId: row.rid })}
          onDelete={this.deleteBatch}
        />
      </Stack>
    );
  };

  render(): React.ReactNode {
    const manager = this.state.manager;
    const selectedBatchId = this.state.selectedBatchId;

    if (selectedBatchId) {
      return (
        <ForecastBaselineWizard
          {...this.props}
          batchid={selectedBatchId}
          onClose={() => this.setState({ selectedBatchId: null }, this.initialize)}
          onChanged={this.initialize}
        />
      );
    }

    const completeAction = manager?.actions.canComplete ? (
      <Button
        variant="contained"
        endIcon={<ArrowForwardIcon />}
        onClick={() => this.setState({ completeOpen: true })}
      >
        Complete Forecasting → Simulation
      </Button>
    ) : null;

    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onBack}
        headerExtension={
          <FPlanningJourney phases={this.state.phases} currentPhaseCode={manager?.context.phasecode || 'FORECASTING'} />
        }
        title="BIE Forecasting"
        subtitle="Create, manage and complete governed Forecast Batches for the active Planning Cycle."
        icon={<AutoGraphIcon />}
        loading={this.state.loading}
        error={this.state.error}
        success={this.state.success}
        warning={this.state.warning}
        onRefresh={this.initialize}
        actions={completeAction}
      >
        {this.renderManager()}

        <Dialog open={this.state.createOpen} onClose={() => this.setState({ createOpen: false })} maxWidth="sm" fullWidth>
          <DialogTitle>Create Forecast Batch</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Batch Name"
                value={this.state.batchname}
                onChange={(event) => this.setState({ batchname: event.target.value })}
                autoFocus
                fullWidth
              />
              <TextField
                label="Historical Window (months)"
                type="number"
                value={this.state.historymonths}
                onChange={(event) => this.setState({ historymonths: Math.max(1, Number(event.target.value) || 60) })}
                helperText="Defaults to 60 months. The server should enforce that this does not exceed the Planning Cycle historical window."
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ createOpen: false })}>Cancel</Button>
            <Button variant="contained" onClick={this.createBatch} disabled={!this.state.batchname.trim() || this.state.loading}>
              Create and Open
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={this.state.completeOpen} onClose={() => this.setState({ completeOpen: false })} maxWidth="sm" fullWidth>
          <DialogTitle>Complete Forecasting</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Forecasting can be completed because at least one published, usable Forecast Baseline is available. Completing the phase will advance the Planning Cycle to BIE Simulation.
              </Alert>
              <TextField
                label="Completion Notes"
                value={this.state.completionNotes}
                onChange={(event) => this.setState({ completionNotes: event.target.value })}
                multiline
                minRows={3}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ completeOpen: false })}>Cancel</Button>
            <Button variant="contained" onClick={this.complete} disabled={this.state.loading}>
              Complete and Continue
            </Button>
          </DialogActions>
        </Dialog>
      </PlanningWorkbenchShell>
    );
  }
}
